import { EMPTY_CHARACTER } from "@pulse/shared-components";
import {
  AutoCompleteInputField,
  AutoCompleteInputFieldProps,
  AutoCompleteItem,
} from "@surya-digital/leo-reactjs-material-ui";
import { Instance } from "mobx-state-tree";
import React, { useEffect, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { SurveyRuleConditionModel } from "../../models/SurveyRuleConditionModel";
import { useGetSurveyRuleKeyOptionsStore } from "../../stores/Hooks";
import { getConditionKeyValidationErrorHelperText } from "../../utils/getSurveyRuleValidationErrorHelperText";
import {
  processSurveyParams,
  useRouteNavigator,
} from "../../../../routes/RoutesHelper";
import { GetSurveyRuleKeyOptionsRPC } from "@pulse/pulse-rpcs/lib/survey-rules/getSurveyRuleKeyOptionsRPC";
import { UnexpectedErrorDialog } from "../../../../components/dialogs/UnexpectedErrorDialog";
import { observer } from "mobx-react";

interface SurveyRuleKeyInputFieldProps
  extends Pick<AutoCompleteInputFieldProps, "onChange"> {
  surveyRuleCondition: Instance<typeof SurveyRuleConditionModel>;
}

export const SOURCE_KEY_FIELD_WIDTH = "280px";

export const SurveyRuleKeyInputField: React.FC<SurveyRuleKeyInputFieldProps> =
  observer(({ onChange, surveyRuleCondition }) => {
    const [openUnexpectedErrorDialog, setOpenUnexpectedErrorDialog] =
      useState(false);
    const [sourceKeyInputValue, setSourceKeyInputValue] = useState(
      surveyRuleCondition.keyType.conditionKey ?? EMPTY_CHARACTER,
    );

    const { t } = useTranslation();
    const navigateTo = useRouteNavigator();
    const sourceKeyFieldId = useId();

    const getSurveyRuleKeyOptionsStore = useGetSurveyRuleKeyOptionsStore();
    const keyOptionsRequestKey = [
      surveyRuleCondition.conditionId,
      surveyRuleCondition.keyType.type,
    ].join(":");

    const getKeyTypeRequestValue =
      (): GetSurveyRuleKeyOptionsRPC.RequestEnums.KeyType.KeyType => {
        switch (surveyRuleCondition.keyType.type) {
          case "QUESTION":
            return GetSurveyRuleKeyOptionsRPC.RequestEnums.KeyType.KeyType
              .QUESTION;
          case "RESPONDENT":
            return GetSurveyRuleKeyOptionsRPC.RequestEnums.KeyType.KeyType
              .RESPONDENT;
          default:
            return surveyRuleCondition.keyType satisfies never;
        }
      };

    useEffect(() => {
      setSourceKeyInputValue(
        surveyRuleCondition.keyType.conditionKey ?? EMPTY_CHARACTER,
      );
    }, [
      surveyRuleCondition.conditionId,
      surveyRuleCondition.keyType.type,
      surveyRuleCondition.keyType.conditionKey,
    ]);

    useEffect(() => {
      const sourceKeyFieldDOMElement =
        document.getElementById(sourceKeyFieldId);
      if (!sourceKeyFieldDOMElement) return;

      const handleSourceKeyFieldFocus = async (): Promise<void> => {
        await processSurveyParams(async (surveyId) => {
          await getSurveyRuleKeyOptionsStore
            .getSurveyRuleKeyOptions(
              surveyId,
              getKeyTypeRequestValue(),
              keyOptionsRequestKey,
            )
            .catch(() => setOpenUnexpectedErrorDialog(true));

          const validationError =
            getSurveyRuleKeyOptionsStore.getSurveyRuleKeyOptionsRPCError.get(
              keyOptionsRequestKey,
            );
          switch (validationError) {
            case undefined:
              // Nothing to handle when there is no validation error.
              break;
            case GetSurveyRuleKeyOptionsRPC.RPCError.InvalidSurveyId:
              navigateTo.pageNotFound();
              break;
            default:
              validationError satisfies never;
          }
        }, navigateTo);
      };

      sourceKeyFieldDOMElement.addEventListener(
        "focus",
        handleSourceKeyFieldFocus,
      );

      return () =>
        sourceKeyFieldDOMElement.removeEventListener(
          "focus",
          handleSourceKeyFieldFocus,
        );
    }, [keyOptionsRequestKey, sourceKeyFieldId]);

    const isLoading =
      !!getSurveyRuleKeyOptionsStore.loadingGetSurveyRuleKeyOptions.get(
        keyOptionsRequestKey,
      );

    const helperText = getConditionKeyValidationErrorHelperText(
      t,
      surveyRuleCondition.keyType.conditionKeyValidationError,
    );

    const getSourceKeyOptions = (): string[] => {
      const keyOption =
        getSurveyRuleKeyOptionsStore.keyOption.get(keyOptionsRequestKey);

      switch (keyOption?.type) {
        case undefined:
          return [];
        case "QUESTION_CODE":
          return Array.from(keyOption.questionCodes);
        case "RESPONDENT_COLUMN_NAME":
          return Array.from(keyOption.respondentColumnNames);
        default:
          return keyOption satisfies never;
      }
    };
    const sourceKeyOptions = getSourceKeyOptions().map((value) => ({
      id: value,
      label: value,
    }));

    const getAutoCompleteValue = (): AutoCompleteItem | null => {
      if (!surveyRuleCondition.keyType.conditionKey) return null;

      return (
        sourceKeyOptions.find(
          (option) => option.id === surveyRuleCondition.keyType.conditionKey,
        ) ?? {
          id: surveyRuleCondition.keyType.conditionKey,
          label: surveyRuleCondition.keyType.conditionKey,
        }
      );
    };

    const handleSourceKeyChange = (
      selectedItem: AutoCompleteItem | null,
    ): void => {
      setSourceKeyInputValue(selectedItem?.label ?? EMPTY_CHARACTER);
      onChange(selectedItem);
    };

    return (
      <>
        {openUnexpectedErrorDialog && (
          <UnexpectedErrorDialog
            message={t("common.uploadDialog.unexpectedErrorDescription")}
            onClickDone={() => setOpenUnexpectedErrorDialog(false)}
          />
        )}
        <AutoCompleteInputField
          id={sourceKeyFieldId}
          label={EMPTY_CHARACTER}
          placeholder={t("surveys.rules.sourceKey")}
          options={sourceKeyOptions}
          value={getAutoCompleteValue()}
          inputValue={sourceKeyInputValue}
          onInputChange={(inputValue) =>
            setSourceKeyInputValue(inputValue ?? EMPTY_CHARACTER)
          }
          onChange={handleSourceKeyChange}
          loading={isLoading}
          loadingText={t("surveys.rules.loadingKeyOptionsText")}
          noOptionsText={t("surveys.autoCompleteField.noResultsFound")}
          error={
            !!surveyRuleCondition.keyType.conditionKeyValidationError.length
          }
          helperText={helperText}
          width={SOURCE_KEY_FIELD_WIDTH}
          minWidth={SOURCE_KEY_FIELD_WIDTH}
        />
      </>
    );
  });
